import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { Establishment } from '../components/establishment-card/establishment.interface';
import { API_ROUTES } from '../consts/api-routes.const';
import { HttpService } from './http.service';

@Injectable({ providedIn: 'root' })
export class EstablishmentService {
  private readonly httpService: HttpService = inject(HttpService);

  public getEstablishments(licensePlate: string): Observable<Establishment[]> {
    return this.httpService
      .get<Establishment[]>(API_ROUTES.PLANNER.establishments(licensePlate))
      .pipe(map((list: Establishment[]): Establishment[] => sortEstablishments(list)));
  }
}

// UI-volgorde: (1) laatst bezochte vestiging altijd bovenaan, (2) vroegst beschikbaar
// eerst (zonder datum achteraan), (3) daarna op plaats en dan straat (Nederlands gesorteerd).
export function sortEstablishments(list: Establishment[]): Establishment[] {
  return [...list].sort(compareEstablishments);
}

function compareEstablishments(a: Establishment, b: Establishment): number {
  const lastVisited: number = Number(!!b.lastVisited) - Number(!!a.lastVisited);
  if (lastVisited !== 0) return lastVisited;

  const rankA: number = availabilityRank(a.availableFrom);
  const rankB: number = availabilityRank(b.availableFrom);
  if (rankA !== rankB) return rankA < rankB ? -1 : 1;

  const city: number = cityOf(a.address).localeCompare(cityOf(b.address), 'nl', {
    sensitivity: 'base',
  });
  if (city !== 0) return city;

  return streetOf(a.address).localeCompare(streetOf(b.address), 'nl', { sensitivity: 'base' });
}

// Ontbrekende/onleesbare beschikbaarheid sorteert achteraan; verder vroegste datum eerst.
function availabilityRank(value: string | undefined): number {
  if (!value) return Number.POSITIVE_INFINITY;
  const time: number = new Date(value).getTime();
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
}

function streetOf(address: string[] | undefined): string {
  return (address?.[0] ?? '').trim();
}

// address[1] = "1234 AB Plaatsnaam" → enkel de plaats (postcode eraf).
function cityOf(address: string[] | undefined): string {
  return (address?.[1] ?? '').replace(/^\s*\d{4}\s*[a-z]{2}\s*/i, '').trim();
}
